import React, { useState } from 'react';
import { useAuth } from './AuthContext';

const containerStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background:
    'radial-gradient(circle at 20% 20%, #f3e8ff, #f7fbf4 35%, #f7fbf4 100%)',
  padding: '24px',
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: '420px',
  borderRadius: '16px',
  border: '1px solid #c8d8c5',
  background: '#ffffff',
  boxShadow: '0 20px 40px rgba(0, 86, 67, 0.08)',
  padding: '28px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid #c8d8c5',
  borderRadius: '10px',
  padding: '11px 12px',
  outline: 'none',
  fontSize: '14px',
};

const buttonStyle: React.CSSProperties = {
  width: '100%',
  border: 'none',
  borderRadius: '10px',
  padding: '12px 14px',
  color: '#ffffff',
  background: '#005643',
  fontWeight: 600,
  cursor: 'pointer',
};

const switchButtonStyle: React.CSSProperties = {
  border: 'none',
  background: 'none',
  color: '#005643',
  cursor: 'pointer',
  fontWeight: 600,
  padding: 0,
};

export function LoginPage() {
  const { login, signup } = useAuth();
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      if (mode === 'signin') {
        await login(username, password);
      } else {
        await signup(username, password, displayName || undefined);
      }
    } catch (submissionError) {
      setError(
        (submissionError as Error).message || (mode === 'signin' ? 'Login failed' : 'Signup failed'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const isSignup = mode === 'signup';

  return (
    <div style={containerStyle}>
      <form style={cardStyle} onSubmit={onSubmit}>
        <h2 style={{ marginTop: 0, marginBottom: '6px' }}>{isSignup ? 'Sign up' : 'Sign in'}</h2>
        <p style={{ marginTop: 0, marginBottom: '20px', color: '#586159' }}>
          {isSignup
            ? 'Create your account to access the portal.'
            : 'Use your username and password to access the portal.'}
        </p>

        {isSignup ? (
          <>
            <label htmlFor="displayName" style={{ display: 'block', marginBottom: '7px' }}>
              Display name (optional)
            </label>
            <input
              id="displayName"
              style={inputStyle}
              value={displayName}
              onChange={event => setDisplayName(event.target.value)}
              autoComplete="name"
            />
          </>
        ) : null}

        <label htmlFor="username" style={{ display: 'block', marginBottom: '7px' }}>
          Username
        </label>
        <input
          id="username"
          style={inputStyle}
          value={username}
          onChange={event => setUsername(event.target.value)}
          autoComplete="username"
          required
        />

        <label
          htmlFor="password"
          style={{ display: 'block', marginTop: '14px', marginBottom: '7px' }}
        >
          Password
        </label>
        <input
          id="password"
          type="password"
          style={inputStyle}
          value={password}
          onChange={event => setPassword(event.target.value)}
          autoComplete="current-password"
          required
        />

        {error ? (
          <p style={{ marginTop: '12px', marginBottom: 0, color: '#bf1f30' }}>{error}</p>
        ) : null}

        <button style={{ ...buttonStyle, marginTop: '18px' }} type="submit" disabled={submitting}>
          {submitting ? (isSignup ? 'Signing up...' : 'Signing in...') : isSignup ? 'Sign up' : 'Sign in'}
        </button>

        <p style={{ marginTop: '12px', marginBottom: 0, color: '#586159', fontSize: '14px' }}>
          {isSignup ? 'Already have an account? ' : "Don't have an account? "}
          <button
            type="button"
            style={switchButtonStyle}
            onClick={() => {
              setError(null);
              setMode(isSignup ? 'signin' : 'signup');
            }}
          >
            {isSignup ? 'Sign in' : 'Sign up'}
          </button>
        </p>
      </form>
    </div>
  );
}
